// Local storage keys
export const STORAGE_KEYS = {
  JWT_TOKEN_STUDENT: 'JWT_TOKEN_STUDENT',
  JWT_TOKEN_INSTRUCTOR: 'JWT_TOKEN_INSTRUCTOR',
  ADDRESS: 'ADDRESS',
  TOKEN: 'TOKEN',
  EMAIL: 'EMAIL'
};

// React-query keys
export const QUERY_KEYS = {
  USER: 'user',
  RECOVERY_CODE: 'code'
};

// Backend Routes
export const BACKEND_KEYS = {
  // auth
  SIGNUP: 'auth/signup',
  SIGNIN: 'auth/signin',
  ACTIVATE_ACCOUNT: 'auth/activate',
  PASSWORD_RESET: 'auth/reset-password',
  FORGOT_PASSWORD: 'auth/password-recovery',
  CHECK_RECOVERY_CODE: 'auth/check-password-recovery-code',
  RESEND_RECOVERY_CODE: 'auth/resend-password-recovery',
  RESEND_ACTIVATION_CODE: 'auth/resend-activation',
  VALIDATE_TOKEN: 'auth/validate-token',
  ME: 'auth/me',
  LOGOUT: 'auth/logout',
  // settings
  USER_INFO: 'user/has-portal',
  CHANGE_EMAIL: 'user/change-email',
  CHANGE_PASSWORD: 'user/change-password',
  DELETE_ACCOUNT: 'user/delete-account',
  RESEND_EMAIL_CONFIRMATION: 'user/resend-email-confirmation',
  CONFiRM_EMAIL: 'user/confirm-email',
  // stripe
  CHECKOUT_SESSION: 'stripe/session',
  GET_USER_PORTAL_URL: 'stripe/customer-portal',
  GET_SUBSCRIPTION_UPDATE_PORTAL_URL: 'stripe/subscription-update-portal',
  GET_FREE_KEY: 'stripe/free-key',
  CLAIM_SUBDIVS_FREE: 'stripe/free-subdivs',
  GET_PLAN_PRICE: 'stripe/price',
  // management
  GET_LICENSE_KEYS: 'get-license-keys',
  RELEASE_ACTIVATIONS: 'license/release-activations',
  PLUGIN: 'user/plugin',
  MY_ACCESS: 'user/me/access',
  // Developer API keys: list (?product=), make, and revoke (/:id).
  DEVELOPER_KEYS: 'developer/keys',
  // 404s unless the account holds a 'standby' grant — the copy and the media
  // live behind it, not in the bundle.
  STANDBY_PAGE: 'standby/page',
  // 404s unless the account holds a 'riser' grant, and 404s for a logged-out
  // caller too rather than 403, so the endpoint never confirms it is there.
  // Every word of the Riser page comes from here; none of it is in the bundle.
  RISER_PAGE: 'riser/page',
  EYE_PACKAGE: 'user/eye-package',
  EYE_VERSIONS: 'user/eye-versions',
  EYE_EULA: 'user/eye-eula',
  EYE_EULA_ACCEPT: 'user/eye-eula/accept',
  EYE_FREE_CLAIM: 'user/eye-free',
  // 403s unless the account holds an 'sbframework' grant AND has accepted the
  // current framework EULA. Per-platform: ?os=windows|mac.
  SBFRAMEWORK_PACKAGE: 'user/sbframework-package',
  SBFRAMEWORK_EULA: 'user/sbframework-eula',
  SBFRAMEWORK_EULA_ACCEPT: 'user/sbframework-eula/accept',
  SBFRAMEWORK_VERSIONS: 'user/sbframework-versions',
  // licensing (Squarebit's own licence server — 404s while LICENSING_ENABLED
  // is off, which the service treats as "no licences" rather than an error)
  MY_LICENSES: 'licensing/my-licenses',
  RELEASE_ACTIVATION: 'licensing/activations',
  ADMIN_LICENSES: 'admin/licenses',
  // Seat count for a LicenseSpring key: PUT admin/licenses/:id/seats
  ADMIN_LICENSE_SEATS: 'admin/licenses',
  OFFLINE_ACTIVATE: 'licensing/offline/activate',
  // dashboard
  PRODUCT: 'spring/products/db-one',
  // admin
  ADMIN_USERS: 'admin/users',
  // library
  DOWNLOAD_STATS: 'library/download-stats',
  TRACK_DOWNLOAD: 'library/track-download',
  // metrics (paths renamed to avoid tracker-blocker heuristics)
  METRICS_ART: 'admin/reports/latency',
  METRICS_ERROR: 'admin/reports/errors',
  METRICS_REVENUE: 'admin/reports/sales',
  METRICS_RPM: 'admin/reports/throughput',
  // license activity (plugin usage reports + per-edition kill switch)
  LICENSE_ACTIVITY: 'admin/license-activity',
  LICENSE_ACTIVITY_SUMMARY: 'admin/license-activity/summary',
  LICENSE_ACTIVITY_CONFIG: 'admin/license-activity/config'
};

export const ROUTER_KEYS = {
  ROOT: '/',
  AUTH: '/auth/*',
  SIGNUP: '/signup',
  ACTIVATE_ACCOUNT: '/activate-account',
  PASSWORD_RECOVERY: '/password-recovery',
  SIGNIN: '/signin',
  FORGOT_PASSWORD: '/forgot-password',
  CREATE_NEW_PASSWORD: '/create-new-password',
  PASSWORD_UPDATED: '/password-updated',
  AUTHORIZED: 'authorized',
  SETTINGS: '/settings',
  MANAGEMENT: '/management',
  MODELS: '/library',
  MODEL_DETAILS: '/library/:id',
  NOT_FOUND: '/not-found',
  NOT_FOUND_CATCHALL: '/*',
  TOUCHPOSE: '/touchpose',
  TOUCHPOSE_DOCS: '/touchpose/docs/*',
  TOUCHPOSE_DOCS_GETTING_STARTED: '/touchpose/docs/getting-started',
  TOUCHPOSE_TUTORIALS: '/touchpose/tutorials',
  TOUCHPOSE_CHANGELOG: '/touchpose/changelog',
  TOUCHPOSE_ROADMAP: '/touchpose/roadmap',
  TOUCHPOSE_FAQ: '/touchpose/faq',
  CONTACT: '/contact',
  RIGGING_SERVICES: '/rigging-services',
  SQUAREBIT_SUBDIVS: '/squarebit-subdivs',
  SQUAREBIT_SUBDIVS_CHANGELOG: '/squarebit-subdivs/changelog',
  SQUAREBIT_SUBDIVS_DOCS: '/squarebit-subdivs/docs/*',
  SQUAREBIT_EYE: '/squarebit-eye',
  SQUAREBIT_EYE_CHANGELOG: '/squarebit-eye/changelog',
  SQUAREBIT_EYE_DOCS: '/squarebit-eye/docs/*',
  // Invite-only, like Riser: every one of these renders the 404 for an account
  // without the 'sbframework' grant, and the page/docs copy is fetched from
  // behind that grant rather than shipped in the bundle.
  SBFRAMEWORK: '/sbframework',
  SBFRAMEWORK_CHANGELOG: '/sbframework/changelog',
  // The editor ships with the framework and is granted with it, so its
  // changelog sits under the framework rather than beside it.
  SBFRAMEWORK_NXT_CHANGELOG: '/sbframework/nxt/changelog',
  SBFRAMEWORK_DOCS: '/sbframework/docs/*',
  SBFRAMEWORK_EULA: '/sbframework/eula',
  // Unannounced — the route renders the 404 for anyone without a grant.
  STANDBY: '/standby',
  // Unannounced, and gated for real: the page has no copy of its own, so
  // without the grant there is nothing to draw and the 404 is what renders.
  RISER: '/riser',
  // Legacy slug kept only as a redirect — old links are still in the wild.
  SQUAREBIT_SUBDIVS_LEGACY: '/unreal-open-subdiv',
  ADMIN: '/admin',
  ADMIN_METRICS: '/admin/metrics',
  ADMIN_LICENSE_ACTIVITY: '/admin/license-activity',
  ADMIN_CHANGELOG: '/admin/changelog'
};

export const ROUTER_LINKS = {
  ROOT: '/',
  AUTH: '/auth/*',
  SIGNUP: '/auth/signup',
  ACTIVATE_ACCOUNT: '/auth/activate-account',
  PASSWORD_RECOVERY: '/auth/password-recovery',
  SIGNIN: '/auth/signin',
  FORGOT_PASSWORD: '/auth/forgot-password',
  CREATE_NEW_PASSWORD: '/auth/create-new-password',
  PASSWORD_UPDATED: '/auth/password-updated',
  AUTHORIZED: 'authorized',
  SETTINGS: '/settings',
  MANAGEMENT: '/management',
  MODELS: '/library',
  MODEL_DETAILS: '/library/:id',
  NOT_FOUND: '/not-found',
  TOUCHPOSE: '/touchpose',
  TOUCHPOSE_DOCS: '/touchpose/docs',
  TOUCHPOSE_DOCS_GETTING_STARTED: '/touchpose/docs/getting-started',
  TOUCHPOSE_TUTORIALS: '/touchpose/tutorials',
  TOUCHPOSE_CHANGELOG: '/touchpose/changelog',
  TOUCHPOSE_ROADMAP: '/touchpose/roadmap',
  TOUCHPOSE_FAQ: '/touchpose/faq',
  CONTACT: '/contact',
  RIGGING_SERVICES: '/rigging-services',
  SQUAREBIT_SUBDIVS: '/squarebit-subdivs',
  SQUAREBIT_SUBDIVS_CHANGELOG: '/squarebit-subdivs/changelog',
  SQUAREBIT_SUBDIVS_DOCS: '/squarebit-subdivs/docs',
  SQUAREBIT_EYE: '/squarebit-eye',
  SQUAREBIT_EYE_CHANGELOG: '/squarebit-eye/changelog',
  SQUAREBIT_EYE_DOCS: '/squarebit-eye/docs',
  SBFRAMEWORK: '/sbframework',
  SBFRAMEWORK_CHANGELOG: '/sbframework/changelog',
  SBFRAMEWORK_NXT_CHANGELOG: '/sbframework/nxt/changelog',
  SBFRAMEWORK_DOCS: '/sbframework/docs',
  // Inside the docs area, so it sits behind the same grant check as the rest
  // of it rather than needing a gate of its own.
  SBFRAMEWORK_API: '/sbframework/docs/api',
  SBFRAMEWORK_EULA: '/sbframework/eula',
  STANDBY: '/standby',
  RISER: '/riser',
  SQUAREBIT_SUBDIVS_LEGACY: '/unreal-open-subdiv',
  ADMIN: '/admin',
  ADMIN_METRICS: '/admin/metrics',
  ADMIN_LICENSE_ACTIVITY: '/admin/license-activity',
  ADMIN_CHANGELOG: '/admin/changelog'
};

export const BREAKPOINTS = {
  tablet: 768,
  // The header collapses to its menu button later than everything else, and
  // needs its own number rather than sharing `tablet`.
  //
  // The bar cannot fit below roughly 1030px in English: the wordmark, four nav
  // items, the theme and language pickers and both auth buttons come to more
  // than that. At 768 it was rendering the full desktop bar anyway, so between
  // there and ~1030 it overflowed and the page scrolled sideways — with Log in
  // and Sign up as the things pushed off the right edge, which is the worst
  // possible thing to lose. A menu button that works beats a bar that hides
  // the sign-up.
  //
  // 1080 rather than 1030 because German is the widest language:
  // "Dienstleistungen" is 50px more than the English "Services". Sized for the
  // worst locale so the bar does not overflow in any of the nine.
  //
  // Deliberately NOT `tablet`: that one also picks which cut of the landing
  // reel to download, and a phone-sized video has nothing to do with how wide
  // the navigation is.
  headerNav: 1080
} as const;

// Ceiling on the seat picker at checkout. Must match MAX_CHECKOUT_QUANTITY in
// the backend's stripe/consts, which both rejects a request above it and sets
// the maximum on Stripe's own quantity stepper. A picker that could produce a
// number either of those refuses would fail at the worst possible moment.
export const MAX_CHECKOUT_QUANTITY = 99;

// Version id of the auto-renewal consent disclosure shown at checkout.
// Must match a key in the backend's AR_CONSENT_TEXTS (stripe/consts) — the
// backend rejects checkout requests carrying an unknown version. Bump BOTH
// together whenever the disclosure/checkbox wording changes.
export const AR_CONSENT_VERSION = 'ar-2026-07-06.2';

// Version id of the Terms of Service / EULA acceptance at signup, recorded
// server-side in terms_acceptances. Bump whenever /terms or the EULA changes
// materially.
export const TERMS_VERSION = 'store-terms-2026-07-24';

// Version id of the Subdivs EULA a free-tier user accepts (clickwrap checkbox) when claiming
// the free plugin. MUST match SUBDIVS_EULA_VERSION in the backend stripe consts.
export const SUBDIVS_EULA_VERSION = 'subdivs-eula-v2.0';

// Version id of the Squarebit Eye EULA a granted account accepts (clickwrap checkbox)
// before its first eye-package download. MUST match EYE_EULA_VERSION in the backend
// user consts — bump both together when /squarebit-eye/eula changes materially.
export const EYE_EULA_VERSION = 'eye-eula-v1.3';

// Version of the Squarebit Framework EULA a granted account must accept
// (clickwrap) before its FIRST framework download. MUST match
// SBFRAMEWORK_EULA_VERSION in the backend's common/constants/sbframework.const.ts.
export const SBFRAMEWORK_EULA_VERSION = 'sbframework-eula-v1.0';
